// Agreement text builder.
//
// Renders a kolabs-style influencer collaboration agreement from operator-
// filled inputs + standardised legal boilerplate. The legal-language
// paragraphs (disclosure, warranties, brand IP licence, indemnification,
// termination, governing law) are STANDARDISED — they're not invented
// commercial terms, they're industry-standard clauses adapted for an
// Australian influencer-marketing context (ACCC + AANA-aware).
//
// COMMERCIAL terms (price, % commission, exclusivity window, usage duration,
// kill fee, etc.) are NEVER defaulted — every commercial value must come from
// explicit operator input or the section is omitted entirely. This is the
// rule we burned ourselves on in the V1 build.
//
// References used for clause language:
//   - LegalVision AU influencer agreement guidance
//   - ACCC influencer guidance (accc.gov.au)
//   - AANA Code of Ethics
//   - PandaDoc + Grin standard templates
//
// Output is plain text optimised for the public sign page <pre> renderer
// and the V2 jsPDF generator. Section numbers auto-increment so skipped
// optional sections don't leave gaps.

// ─── Currencies (operator selects) ────────────────────────────────────
export const CURRENCIES = [
  { code: "AUD", symbol: "AU$", label: "AUD — Australian Dollar" },
  { code: "USD", symbol: "US$", label: "USD — US Dollar" },
  { code: "GBP", symbol: "£",   label: "GBP — British Pound" },
  { code: "EUR", symbol: "€",   label: "EUR — Euro" },
  { code: "NZD", symbol: "NZ$", label: "NZD — NZ Dollar" },
  { code: "CAD", symbol: "C$",  label: "CAD — Canadian Dollar" },
];

export const currencySymbol = (code) =>
  (CURRENCIES.find(c => c.code === code) || {}).symbol || code;

// ─── Standard legal boilerplate ───────────────────────────────────────
// These are operator-editable defaults rendered when the relevant section
// is enabled in the form. NOT commercial policy — they're standard legal
// language. Operators can paste their own legal text on top of these if
// they have lawyer-reviewed versions.

const CLAUSE = {
  disclosure: (tag) => `The Creator must clearly and prominently disclose the commercial nature of the Content in line with the AANA Code of Ethics and the ACCC's guidance on social media advertising. Disclosure must use ${tag ? `"${tag}"` : "an appropriate disclosure label (e.g. #ad, 'Paid partnership with…', or the platform's branded content tools)"} placed where viewers can see it without expanding the caption or tapping "more". The Creator is responsible for ensuring all claims in the Content are accurate, substantiated, and compliant with the Australian Consumer Law.`,

  creatorWarranties: () => `The Creator warrants that:\n(a) the Content is original and created solely by the Creator;\n(b) the Content does not infringe any third party's intellectual property, privacy, publicity, or moral rights;\n(c) the Creator has obtained all consents, licences, and clearances required for any music, persons, locations, trademarks, or other third-party material featured in the Content; and\n(d) the Content complies with all applicable laws and the host platform's terms of service.`,

  brandIpLicence: () => `The Brand grants the Creator a limited, non-exclusive, non-transferable, royalty-free licence to use the Brand's name, logo, product imagery, and any other brand materials supplied (the "Brand Materials") solely to produce and publish the Content under this Agreement. All goodwill arising from the Creator's use of the Brand Materials accrues to the Brand. This licence ends on completion or termination of this Agreement.`,

  ipTransfer: () => `The Creator assigns to the Brand all intellectual property rights (including copyright) in the Content created under this Agreement, with effect from the moment of creation. To the maximum extent permitted by law, the Creator waives any moral rights in the Content. The Brand may use, modify, reproduce, distribute, publicly display, and publicly perform the Content for any lawful purpose without further consent or payment.`,

  creatorRetainsIp: () => `The Creator retains ownership of all intellectual property rights in the Content. The Brand's right to use the Content is limited to the Usage Rights granted under this Agreement. Outside the scope of those rights, the Brand may not modify, repurpose, or redistribute the Content without the Creator's prior written consent.`,

  confidentiality: () => `During and after this Agreement, the Creator agrees to keep confidential any non-public information learned about the Brand, including business plans, customer data, financial information, supplier details, and creative briefs ("Confidential Information"). The Creator will use Confidential Information solely to perform under this Agreement and will not disclose it to any third party without the Brand's prior written consent, except as required by law.`,

  termination: (noticeDays) => `Either party may terminate this Agreement by giving ${noticeDays || "seven (7)"} days' written notice if the other party materially breaches it and fails to remedy the breach within seven (7) days of being notified. The Brand may terminate this Agreement immediately if the Creator engages in conduct that brings, or is reasonably likely to bring, the Brand into disrepute. On termination, the Brand will pay for any Content already delivered and accepted up to the termination date, and the Creator will promptly return or destroy any Brand Materials in their possession.`,

  indemnification: () => `Each party indemnifies the other against any third-party claims, losses, damages, costs, or expenses (including reasonable legal fees) arising from that party's breach of this Agreement, negligence, wilful misconduct, or breach of warranties. Neither party is liable for indirect, incidental, special, or consequential loss. Each party's total aggregate liability under this Agreement is capped at the total Fees paid or payable under this Agreement.`,

  governingLaw: (jurisdictionLabel) => `This Agreement is governed by and construed in accordance with the laws of ${jurisdictionLabel}. The parties submit to the exclusive jurisdiction of the courts of that jurisdiction and any courts of appeal from them.`,

  entireAgreement: () => `This Agreement is the entire agreement between the parties about its subject matter and supersedes all prior discussions, representations, and agreements. Any amendment must be in writing and signed by both parties. If any provision is found unenforceable, the remaining provisions continue in full force. Nothing in this Agreement creates a partnership, joint venture, or employment relationship between the parties.`,
};

// ─── Helpers ──────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-AU", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
};

const todayLong = () =>
  new Date().toLocaleDateString("en-AU", {
    day: "numeric", month: "long", year: "numeric",
  });

const bullets = (text) =>
  (text || "")
    .split("\n")
    .map(s => s.trim())
    .filter(Boolean)
    .map(s => "• " + s)
    .join("\n");

const auStateLabel = (state) => {
  const map = {
    NSW: "New South Wales",
    VIC: "Victoria",
    QLD: "Queensland",
    WA:  "Western Australia",
    SA:  "South Australia",
    TAS: "Tasmania",
    ACT: "Australian Capital Territory",
    NT:  "Northern Territory",
  };
  return map[state] || state;
};

// Produce the human-readable jurisdiction label for the governing-law clause.
// E.g. "New South Wales, Australia" or "England and Wales" or just "United States".
const jurisdictionLabel = (form) => {
  if (form.jurisdiction === "Australia" && form.jurisdictionState) {
    return `${auStateLabel(form.jurisdictionState)}, Australia`;
  }
  return form.jurisdiction || "Australia";
};

// ─── Main builder ─────────────────────────────────────────────────────
// Returns a single string — the full rendered agreement text. Caller is
// responsible for stashing this on the agreement row as content_snapshot.
export function buildAgreementText({ form, brand, creator }) {
  const sections = [];
  let n = 1;
  const push = (title, body) => {
    if (!body) return;
    sections.push(`${n++}. ${title.toUpperCase()}\n${body}`);
  };

  const collabLabel =
    form.collabType === "Other"
      ? form.collabTypeOther || "Other"
      : form.collabType;

  const brandName = form.brandLegalName || brand?.name || "Brand";
  const creatorName = form.creatorLegalName || creator?.name || "Creator";
  const creatorHandle = creator?.handle ? `@${creator.handle}` : "—";

  // ─── Header ───
  sections.push(
    `COLLABORATION AGREEMENT\n` +
    `${brandName} × ${creatorName}\n` +
    `Date: ${todayLong()}`
  );

  // Optional warm intro
  if (form.introNote && form.introNote.trim()) {
    sections.push(form.introNote.trim());
  } else {
    sections.push(
      `This Agreement sets out the terms on which ${brandName} (the "Brand") and ${creatorName} (the "Creator") will work together on the collaboration described below. Please read it carefully before signing.`
    );
  }

  // 1. PARTIES
  const brandLine = `Brand: ${brandName}` + (form.brandAbn ? ` (ABN ${form.brandAbn})` : "");
  const creatorLine = `Creator: ${creatorName}` + (form.creatorAbn ? ` (ABN ${form.creatorAbn})` : "") + ` — Instagram: ${creatorHandle}`;
  push("Parties", `${brandLine}\n${creatorLine}`);

  // 2. SCOPE OF WORK
  const scopeLines = [];
  scopeLines.push(`Collaboration type: ${collabLabel}.`);
  if (form.deliverables && form.deliverables.trim()) {
    scopeLines.push("Deliverables:\n" + bullets(form.deliverables));
  }
  if (form.briefDeliveryDate) {
    scopeLines.push(`Draft for review by: ${fmtDate(form.briefDeliveryDate)}.`);
  }
  if (form.postingDeadline) {
    scopeLines.push(`Posting deadline: ${fmtDate(form.postingDeadline)}.`);
  }
  push("Scope of work", scopeLines.join("\n\n"));

  // 3. CONTENT GUIDELINES
  if (form.contentGuidelines && form.contentGuidelines.trim()) {
    push("Content guidelines", bullets(form.contentGuidelines));
  }

  // 4. DISCLOSURE
  if (form.includeDisclosure) {
    push("Disclosure", CLAUSE.disclosure(form.disclosureTag));
  }

  // 5. COMPENSATION
  const isPaid = form.collabType === "Paid" || form.collabType === "Long-term content creator";
  if (isPaid && form.paymentAmount) {
    const sym = currencySymbol(form.currency);
    const gstSuffix = form.gstApplicable ? " (plus GST if applicable — Creator to issue a tax invoice)" : "";
    const payLines = [`Fee: ${sym}${form.paymentAmount} ${form.currency || ""}${gstSuffix}.`];
    if (form.paymentTerms) payLines.push(`Payment terms: ${form.paymentTerms}.`);
    if (form.paymentMethod) payLines.push(`Payment method: ${form.paymentMethod}.`);
    if (form.commissionRate && form.commissionRate.trim()) {
      payLines.push(`Affiliate commission: ${form.commissionRate.trim()} of net sales attributed to the Creator's tracked link or discount code.`);
    }
    if (form.audienceDiscount && form.audienceDiscount.trim()) {
      payLines.push(`Audience discount code: ${form.audienceDiscount.trim()}.`);
    }
    push("Compensation", payLines.join("\n"));
  } else if (form.collabType === "Gifted") {
    const giftLines = ["Gifted product only. No monetary payment is due under this Agreement."];
    if (form.commissionRate && form.commissionRate.trim()) {
      giftLines.push(`Affiliate commission: ${form.commissionRate.trim()} of net sales attributed to the Creator.`);
    }
    if (form.audienceDiscount && form.audienceDiscount.trim()) {
      giftLines.push(`Audience discount code: ${form.audienceDiscount.trim()}.`);
    }
    push("Compensation", giftLines.join("\n"));
  }

  // 6. USAGE RIGHTS
  if (form.usageRights) {
    const lines = [];
    if (form.usageRights === "Full transfer") {
      lines.push(`The Creator assigns all rights in the Content to the Brand (see Intellectual Property below).`);
    } else {
      const dur = form.usageDuration ? ` for ${form.usageDuration}` : "";
      const terr = form.usageTerritory ? ` in ${form.usageTerritory}` : "";
      lines.push(`The Brand may use the Content for ${form.usageRights.toLowerCase()} purposes${dur}${terr}.`);
    }
    if (form.whitelisting) {
      const wDur = form.whitelistingDuration ? ` for ${form.whitelistingDuration}` : "";
      lines.push(`Paid amplification (whitelisting / Meta Branded Content / TikTok Spark Ads) is granted${wDur}. The Creator will provide the relevant code or access on request.`);
    }
    push("Usage rights", lines.join("\n\n"));
  }

  // 7. EXCLUSIVITY
  if ((form.exclusivityCategories && form.exclusivityCategories.trim()) || form.exclusivityWindow) {
    const cats = form.exclusivityCategories && form.exclusivityCategories.trim()
      ? form.exclusivityCategories.trim()
      : "the Brand's category";
    const win = form.exclusivityWindow ? ` for ${form.exclusivityWindow}` : "";
    push(
      "Exclusivity",
      `During the term of this Agreement${win}, the Creator agrees not to create paid promotional content for any direct competitor of the Brand in ${cats}.`
    );
  }

  // 8. APPROVAL & REVISIONS
  if (form.approvalRounds || form.killFee) {
    const lines = [];
    if (form.approvalRounds) {
      lines.push(`The Brand has the right to review and request changes before the Content is posted. ${form.approvalRounds} round(s) of revisions are included. The Brand will return feedback within 3 business days of receiving a draft.`);
    }
    if (form.killFee && form.killFee.trim()) {
      lines.push(`Kill fee: if the Brand cancels the Content after a draft has been delivered, the Creator is entitled to ${form.killFee.trim()} of the Fee.`);
    }
    push("Approval & revisions", lines.join("\n\n"));
  }

  // 9. INTELLECTUAL PROPERTY
  if (form.ipTransferToBrand || form.usageRights === "Full transfer") {
    push("Intellectual property", CLAUSE.ipTransfer());
  } else {
    push("Intellectual property", CLAUSE.creatorRetainsIp() + "\n\n" + CLAUSE.brandIpLicence());
  }

  // 10. CONFIDENTIALITY
  if (form.includeConfidentiality) {
    push("Confidentiality", CLAUSE.confidentiality());
  }

  // 11. CREATOR WARRANTIES
  if (form.includeWarranties) {
    push("Creator warranties", CLAUSE.creatorWarranties());
  }

  // 12. INDEMNIFICATION & LIABILITY
  if (form.includeIndemnification) {
    push("Indemnification & liability", CLAUSE.indemnification());
  }

  // 13. TERM & TERMINATION
  if (form.includeTermination) {
    push("Term & termination", CLAUSE.termination(form.noticePeriodDays));
  }

  // 14. GOVERNING LAW
  push("Governing law", CLAUSE.governingLaw(jurisdictionLabel(form)));

  // 15. ENTIRE AGREEMENT
  push("Entire agreement", CLAUSE.entireAgreement());

  // Signature block (visual — actual signing is captured digitally on the public sign page)
  sections.push(
    `SIGNED\n\n` +
    `For the Brand: ${brand?.team?.[0]?.name || "Brand representative"} on behalf of ${brandName}\n` +
    `Date: ${todayLong()}\n\n` +
    `By the Creator: ${creatorName} (${creatorHandle})\n` +
    `Date: [to be signed]`
  );

  return sections.join("\n\n");
}

// Default form state. Commercial fields are empty; legal boilerplate
// opt-ins default ON (these protect both parties and are industry-standard
// — leaving them off is the riskier choice). The operator can untick if
// they have a reason.
export const DEFAULT_AGREEMENT_FORM = {
  collabType: "Gifted",
  collabTypeOther: "",
  introNote: "",
  deliverables: "",
  contentGuidelines: "",
  briefDeliveryDate: "",
  postingDeadline: "",
  currency: "AUD",
  paymentAmount: "",
  paymentMethod: "",
  paymentTerms: "",
  gstApplicable: false,
  commissionRate: "",
  audienceDiscount: "",
  usageRights: "",
  usageDuration: "",
  usageTerritory: "",
  whitelisting: false,
  whitelistingDuration: "",
  exclusivityCategories: "",
  exclusivityWindow: "",
  approvalRounds: "",
  killFee: "",
  includeDisclosure: true,
  includeWarranties: true,
  includeIndemnification: true,
  includeTermination: true,
  includeConfidentiality: false,
  ipTransferToBrand: false,
  noticePeriodDays: "7",
  disclosureTag: "#ad — Paid Partnership",
  creatorLegalName: "",
  creatorAbn: "",
  brandLegalName: "",
  brandAbn: "",
  jurisdiction: "Australia",
  jurisdictionState: "NSW",
};
